<script setup>
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
import {PhCaretLeft, PhCaretRight, PhX} from '@phosphor-icons/vue';

const props = defineProps({
    show: Boolean,
    images: Array,
    alt: String,
});

const emit = defineEmits(['close']);

const index = defineModel('index', {type: Number, default: 0});

const zoomed = ref(false);
const canZoom = ref(false);
const scroller = ref(null);
const fitImage = ref(null);

const hasMany = computed(() => props.images.length > 1);

const close = () => emit('close');

// Zoom only helps when the fit view is showing the image below its native size.
const measure = () => {
    const img = fitImage.value;
    if (!img?.complete) {
        return;
    }
    const rect = img.getBoundingClientRect();
    canZoom.value = img.naturalWidth > rect.width + 1 || img.naturalHeight > rect.height + 1;
};

const step = (delta) => {
    zoomed.value = false;
    canZoom.value = false;
    index.value = (index.value + delta + props.images.length) % props.images.length;
};

// Switch to native size, scrolled so the clicked point stays centered.
const toggleZoom = async (e) => {
    if (zoomed.value) {
        zoomed.value = false;
        return;
    }
    if (!canZoom.value) {
        return;
    }

    const rect = e.target.getBoundingClientRect();
    const ratioX = (e.clientX - rect.left) / rect.width;
    const ratioY = (e.clientY - rect.top) / rect.height;

    zoomed.value = true;
    await nextTick();

    const el = scroller.value;
    el.scrollLeft = ratioX * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = ratioY * el.scrollHeight - el.clientHeight / 2;
};

const onKeydown = (e) => {
    if (!props.show) {
        return;
    }
    if (e.key === 'Escape') {
        close();
    }
    if (!hasMany.value) {
        return;
    }
    if (e.key === 'ArrowLeft') {
        step(-1);
    }
    if (e.key === 'ArrowRight') {
        step(1);
    }
};

watch(() => props.show, (show) => {
    zoomed.value = false;
    document.body.style.overflow = show ? 'hidden' : null;
});

onMounted(() => {
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('resize', measure);
});

onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown);
    window.removeEventListener('resize', measure);
    document.body.style.overflow = null;
});
</script>

<template>
    <Teleport to="body">
        <Transition
            enter-active-class="ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="ease-in duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div v-if="show" class="fixed inset-0 z-50 bg-black/90" role="dialog" aria-modal="true" :aria-label="alt">
                <div ref="scroller" class="absolute inset-0 overflow-auto" @click.self="close">
                    <div v-if="zoomed" class="flex w-max min-w-full min-h-full" @click.self="close">
                        <img :src="images[index]" :alt="alt" @click="toggleZoom"
                             class="m-auto max-w-none cursor-zoom-out"/>
                    </div>
                    <img v-else ref="fitImage" :src="images[index]" :alt="alt" @click="toggleZoom" @load="measure"
                         class="absolute inset-0 m-auto max-w-[calc(100%-2rem)] max-h-[calc(100%-2rem)] sm:max-w-[calc(100%-6rem)] sm:max-h-[calc(100%-6rem)]"
                         :class="{'cursor-zoom-in': canZoom}"/>
                </div>

                <button type="button" @click="close" title="Close"
                        class="absolute top-3 right-3 rounded-full bg-black/50 p-2 text-white hover:bg-black/70">
                    <PhX class="h-6 w-6"/>
                </button>

                <template v-if="hasMany">
                    <button type="button" @click="step(-1)" title="Previous image"
                            class="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70">
                        <PhCaretLeft class="h-6 w-6"/>
                    </button>
                    <button type="button" @click="step(1)" title="Next image"
                            class="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70">
                        <PhCaretRight class="h-6 w-6"/>
                    </button>
                    <div class="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-sm text-white">
                        {{ index + 1 }} / {{ images.length }}
                    </div>
                </template>
            </div>
        </Transition>
    </Teleport>
</template>
